import { UseFormReturn } from 'react-hook-form';
import { Grid, Typography } from '@mui/material';
import { PatientInput } from '../../../generated/graphql';
import { DemographicFormValues } from './demographics-form';
import {
  AssessmentCard,
  ControlledDateField,
  ControlledMaskedField,
  ControlledTextField,
} from '../shared';
import { dayToDate } from '../../../helpers/day';
import { rules } from './rules';
import { SexLabelValueMap } from '../../../helpers/assessment';
import ControlledRadioGroupField from '../shared/controlled-radio-group-field';

type PropTypes = {
  form: UseFormReturn<DemographicFormValues, any>;
  patient: PatientInput;
  isGuardian: boolean;
  isReferral: boolean;
  showSexField: boolean;
};

const SEX_OPTIONS = Object.keys(SexLabelValueMap)
  .filter(label => label !== SexLabelValueMap[SexLabelValueMap.Unknown])
  .map(label => ({ label, value: SexLabelValueMap[label] }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const DemographicsBasics = ({
  form,
  patient,
  isGuardian,
  isReferral,
  showSexField,
}: PropTypes) => {
  const { control } = form;

  return (
    <AssessmentCard>
      <Grid container columnSpacing={1} rowSpacing={2}>
        {showSexField && (
          <Grid item xs={12}>
            <Typography component="h3" variant="h5">
              {isGuardian ? 'Patient’s information' : 'Your information'}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <ControlledTextField<DemographicFormValues>
            name="firstName"
            control={control}
            rules={rules.firstName}
            defaultValue={patient.firstName ?? ''}
            disabled={isReferral}
            autoComplete="on"
            label="Legal first name"
            inputProps={{ maxLength: 50 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledTextField<DemographicFormValues>
            name="middleName"
            control={control}
            defaultValue={patient.middleName ?? ''}
            autoComplete="on"
            label="Middle name (optional)"
            inputProps={{ maxLength: 50 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledTextField<DemographicFormValues>
            name="lastName"
            control={control}
            rules={rules.lastName}
            defaultValue={patient.lastName ?? ''}
            autoComplete="on"
            label="Last name"
            inputProps={{ maxLength: 50 }}
            disabled={isReferral}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledDateField<DemographicFormValues>
            name="birthDate"
            form={form}
            rules={rules.birthDate}
            defaultValue={
              patient.birthDate
                ? dayToDate(patient.birthDate).toLocaleDateString()
                : null
            }
            disabled={isReferral}
            isReferral={isReferral}
            label="Date of birth"
            disableFuture
          />
        </Grid>
        {!isGuardian && (
          <Grid item xs={12} sm={6}>
            <ControlledMaskedField<DemographicFormValues>
              name="phone"
              control={control}
              rules={rules.phone}
              defaultValue={patient.phone ?? ''}
              label="Phone"
              mask="(999) 999-9999"
            />
          </Grid>
        )}
        {showSexField && (
          <Grid item xs={12} sm={6}>
            <ControlledRadioGroupField<DemographicFormValues>
              name="sex"
              control={control}
              defaultValue={patient.sex ?? ''}
              rules={rules.sex}
              options={SEX_OPTIONS}
              label="Sex on file with insurer"
            />
          </Grid>
        )}
      </Grid>
    </AssessmentCard>
  );
};
